import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { promptLogin } from "@/lib/authRedirect";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Check, Users, MessageCircle, Sparkles } from "lucide-react";
import SEO from "@/components/site/SEO";
import DiscussionCard from "@/components/site/DiscussionCard";
import ComposeDiscussionModal from "@/components/site/ComposeDiscussionModal";

/**
 * Halaman Ruang — /forum/ruang/:slug
 * Dark navy hero → members / new-week meta → CTA join → highlighted intro prompt →
 * sort tabs (Terbaru | Populer | Belum terjawab) → discussion list.
 * Sticky FAB "+ Mulai diskusi di Ruang ini" opens ComposeDiscussionModal.
 */
const SORTS = [
  { key: "terbaru", label: "Terbaru" },
  { key: "populer", label: "Populer" },
  { key: "belum-terjawab", label: "Belum terjawab" },
];

export default function RuangDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sort, setSort] = useState("terbaru");
  const [joined, setJoined] = useState(false);
  const [joinPending, setJoinPending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = async (nextSort = sort) => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/ruang/${slug}?sort=${nextSort}`),
        user ? api.get("/me/memberships").catch(() => ({ data: { ruang_ids: [] } })) : Promise.resolve({ data: { ruang_ids: [] } }),
      ]);
      setData(r1.data);
      setJoined((r2.data.ruang_ids || []).includes(r1.data.ruang.ruang_id));
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "Ruang tidak ditemukan." : "Gagal memuat Ruang.");
      if (e?.response?.status === 404) nav("/forum/ruang", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load("terbaru"); setSort("terbaru"); /* eslint-disable-line */ }, [slug, user]);

  const changeSort = (k) => {
    setSort(k);
    load(k);
  };

  const toggleJoin = async () => {
    if (!user) { promptLogin(nav); return; }
    setJoinPending(true);
    const wasJoined = joined;
    setJoined(!wasJoined); // optimistic
    try {
      await api.post(`/ruang/${data.ruang.ruang_id}/${wasJoined ? "leave" : "join"}`);
      // Refresh member_count in UI
      setData((d) => d ? { ...d, ruang: { ...d.ruang, member_count: Math.max(0, d.ruang.member_count + (wasJoined ? -1 : 1)) } } : d);
    } catch (e) {
      setJoined(wasJoined);
      toast.error(e?.response?.data?.detail || "Gagal memperbarui keanggotaan.");
    } finally {
      setJoinPending(false);
    }
  };

  const onComposed = (newDiscussion) => {
    // Prepend so user sees it immediately in "Terbaru"
    setData((d) => d ? { ...d, discussions: [newDiscussion, ...d.discussions] } : d);
    if (data?.ruang) {
      setData((d) => d ? { ...d, ruang: { ...d.ruang, discussion_count: (d.ruang.discussion_count || 0) + 1, topic_count: (d.ruang.topic_count || 0) + 1 } } : d);
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-brand-alabaster">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  const r = data.ruang;

  return (
    <div className="min-h-screen bg-brand-alabaster pb-32" data-testid="ruang-detail-page">
      <SEO title={`${r.name} — Ruang di The School Reports`} description={r.description} />

      {/* Hero — dark navy */}
      <div className="bg-brand-primary text-white">
        <div className="max-w-2xl mx-auto px-5 pt-6 pb-8">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => nav("/forum/ruang")} aria-label="Kembali ke Jelajah Ruang" data-testid="ruang-detail-back" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-white/60">Ruang</span>
          </div>

          <div className="text-4xl mb-3" aria-hidden>{r.icon}</div>
          <h1 className="font-serif-display text-3xl md:text-4xl leading-tight mb-3">{r.name}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/80 mb-4">
            <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> {formatCount(r.member_count)} anggota</span>
            <span className="opacity-40">·</span>
            <span className="inline-flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> {r.topic_count ?? r.discussion_count ?? 0} diskusi</span>
            {data.new_this_week > 0 && (
              <>
                <span className="opacity-40">·</span>
                <span className="inline-flex items-center gap-1.5 text-emerald-300"><Sparkles className="w-4 h-4" /> {data.new_this_week} baru minggu ini</span>
              </>
            )}
          </div>
          <p className="text-white/85 leading-relaxed text-[15px] mb-5">{r.description}</p>

          <button
            onClick={toggleJoin}
            disabled={joinPending}
            data-testid="ruang-detail-join-btn"
            className={`w-full h-12 rounded-full font-semibold text-sm transition-colors ${
              joined
                ? "bg-white/10 border border-white/20 text-white hover:bg-white/20"
                : "bg-brand-accent hover:bg-brand-accent-hover text-white"
            } disabled:opacity-50`}
          >
            {joined ? (
              <span className="inline-flex items-center gap-2"><Check className="w-4 h-4" strokeWidth={3} /> Tergabung · Notifikasi aktif</span>
            ) : (
              "+ Gabung Ruang"
            )}
          </button>
        </div>
      </div>

      {/* Intro prompt card — sits on alabaster surface with clean spacing below the navy hero.
          Yellow background per §9 typography spec: soft yellow #FFFDF5 + border #F0E4BF for
          WCAG-safe contrast against near-black text. */}
      {r.intro_prompt && (
        <div className="max-w-2xl mx-auto px-5 pt-6">
          <div className="rounded-2xl bg-[#FFFDF5] border border-[#F0E4BF] border-l-4 border-l-brand-accent p-5 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.10em] text-[#C15C3F] mb-2">
              📌 DISKUSI PENGANTAR
            </p>
            <p className="font-sans font-semibold text-[15px] text-[#1C1C1F] leading-snug">
              {r.intro_prompt}
            </p>
          </div>
        </div>
      )}

      {/* Sort tabs */}
      <div className="max-w-2xl mx-auto px-5 mt-6">
        <div className="flex items-center gap-6 border-b border-brand-border">
          {SORTS.map((s) => {
            const on = sort === s.key;
            return (
              <button
                key={s.key}
                onClick={() => changeSort(s.key)}
                data-testid={`ruang-sort-${s.key}`}
                className={`pb-3 -mb-px text-sm font-semibold border-b-2 transition-colors ${
                  on ? "border-brand-accent text-brand-accent" : "border-transparent text-brand-text-muted hover:text-brand-text"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Discussion list */}
      <div className="max-w-2xl mx-auto px-5 mt-5 space-y-3">
        {data.discussions.length === 0 ? (
          <div className="rounded-2xl bg-white border border-brand-border p-8 text-center shadow-sm" data-testid="ruang-empty-state">
            <p className="text-brand-text-muted mb-4">
              {sort === "belum-terjawab"
                ? "Belum ada pertanyaan yang menunggu jawaban di Ruang ini."
                : "Belum ada diskusi di Ruang ini. Jadilah yang pertama memulai."}
            </p>
            <button onClick={() => (user ? setComposeOpen(true) : promptLogin(nav))} className="text-brand-accent font-semibold text-sm hover:underline">
              Mulai diskusi pertama →
            </button>
          </div>
        ) : (
          data.discussions.map((d) => <DiscussionCard key={d.discussion_id} d={d} showRuangBadge={false} />)
        )}
      </div>

      {/* FAB — sits above the mobile bottom tab bar (~64px + safe-area) so it never overlaps. */}
      <button
        onClick={() => (user ? setComposeOpen(true) : promptLogin(nav))}
        data-testid="ruang-fab-compose"
        style={{ bottom: "calc(1.5rem + 64px + env(safe-area-inset-bottom))" }}
        className="fixed right-6 md:!bottom-6 sm:right-1/2 sm:translate-x-[calc(20rem-1.5rem)] h-12 px-5 rounded-full bg-brand-accent hover:bg-brand-accent-hover text-white font-semibold text-sm shadow-lg transition-transform hover:scale-105"
      >
        + Mulai diskusi di Ruang ini
      </button>

      {/* Compose modal — locks the Ruang tujuan */}
      <ComposeDiscussionModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        ruang={r}
        onCreated={onComposed}
      />
    </div>
  );
}

function formatCount(n) {
  const num = Number(n || 0);
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}rb`;
  return String(num);
}
